import { ChevronDown, ChevronRight, Pencil, TriangleAlert } from 'lucide-react';
import { useId, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useAddressPlan } from '../api/queries';
import type { AddressPlan, Diagnostic, SubnetUse } from '../api/types';
import { Button, CopyButton, ErrorMessage, Loading, StatusBadge } from '../components/ui';
import { formatDay } from '../lib/format';
import { usePhone } from '../shell/breakpoints';
import { strings } from '../strings';
import { parseCidr, percentText, shareOf } from './lists/cidr';
import { EditAddressPlanDialog } from './lists/EditAddressPlanDialog';
import { attachmentPresentation, roleSubnets, SIZE_PREFIX, useManageNetworks } from './shared';
import type { NetworkTabProps } from './tabProps';

const s = strings.networks;
const a = s.lists.addressPlan;

type Region = AddressPlan['regions'][number];
type PlanAttachment = Region['attachments'][number];

/** The attachment a diagnostic points into (`/regions/<i>/attachments/<j>…`), if any. */
function attachmentOf(plan: AddressPlan, d: Diagnostic): PlanAttachment | null {
  const match = /^\/regions\/(\d+)\/attachments\/(\d+)/.exec(d.path ?? '');
  if (!match) return null;
  return plan.regions[Number(match[1])]?.attachments[Number(match[2])] ?? null;
}

function capacityOf(attachment: PlanAttachment, role: string): number | null {
  return roleSubnets(attachment.cidr).find((r) => r.role === role)?.capacity ?? null;
}

function tooSmall(attachment: PlanAttachment): SubnetUse[] {
  return attachment.subnets.filter((sub) => {
    const capacity = capacityOf(attachment, sub.role);
    return sub.estimatedNeed !== null && capacity !== null && sub.estimatedNeed > capacity;
  });
}

function Cidr({ cidr }: { cidr: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="font-mono">{cidr}</span>
      <CopyButton value={cidr} label={a.copy(cidr)} />
    </span>
  );
}

function Need({ attachment, subnet }: { attachment: PlanAttachment; subnet: SubnetUse }) {
  const capacity = capacityOf(attachment, subnet.role);
  if (subnet.estimatedNeed === null || capacity === null) return null;
  const over = subnet.estimatedNeed > capacity;
  return (
    <span className={over ? 'net-need-over' : 'text-fg-muted'}>
      {over && <TriangleAlert aria-hidden className="mr-1 inline size-3.5" />}
      {a.need(subnet.estimatedNeed, capacity)}
    </span>
  );
}

/** A supernet's bar (`role="img"` with the numbers as its name): reserved ranges hatched, region blocks in the accent. */
function SupernetBar({
  plan,
  supernet,
  lengths,
}: {
  plan: AddressPlan;
  supernet: AddressPlan['supernets'][number];
  lengths: Lengths;
}) {
  const parsed = parseCidr(supernet.cidr);
  const regionPrefix = lengths.region;
  const blocks = plan.regions.flatMap((r) => r.blocks).filter((b) => shareOf(supernet.cidr, b));
  const total = parsed && regionPrefix >= parsed.prefix ? 2 ** (regionPrefix - parsed.prefix) : blocks.length;
  const reserved = plan.reserved
    .map((r) => ({ ...r, share: shareOf(supernet.cidr, r.cidr) }))
    .filter((r) => r.share);
  const reservedPercent = reserved.reduce((sum, r) => sum + (r.share?.width ?? 0), 0);
  const allocated = percentText(supernet.allocatedPercent);
  const reservedText = percentText(reservedPercent);
  return (
    <div className="net-supernet">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p>
          <span className="font-semibold">{a.supernet}</span>{' '}
          <span className="font-mono">{supernet.cidr}</span>
        </p>
        <p className="text-sm text-fg-muted">
          {a.allocatedLine(blocks.length, total, allocated, reservedText)}
        </p>
      </div>
      <div
        role="img"
        aria-label={a.bar(supernet.cidr, blocks.length, total, allocated, reservedText)}
        className="net-bar"
      >
        {reserved.map((r) => (
          <span
            key={r.cidr}
            className="net-bar-reserved"
            style={{ left: `${r.share!.offset}%`, width: `${r.share!.width}%` }}
          />
        ))}
        {blocks.map((b) => {
          const share = shareOf(supernet.cidr, b)!;
          return (
            <span
              key={b}
              className="net-bar-block"
              style={{ left: `${share.offset}%`, width: `${share.width}%` }}
            />
          );
        })}
      </div>
      <p className="mt-1 flex flex-wrap gap-x-4 text-xs text-fg-muted">
        {reserved.map((r) => (
          <span key={r.cidr}>
            <span aria-hidden className="net-legend-reserved" /> {a.legendReserved(r.cidr, r.label)}
          </span>
        ))}
        <span>
          <span aria-hidden className="net-legend-block" /> {a.legendRegions}
        </span>
        <span>{a.legendLengths(lengths.region, lengths.hub, lengths.size, lengths.prefix)}</span>
      </p>
    </div>
  );
}

/** The plan's prefix lengths and default size (from the network's settings). */
interface Lengths {
  region: number;
  hub: number;
  size: string;
  prefix: number;
}

interface Row {
  key: string;
  level: number;
  parent: string | null;
  expandable: boolean;
  name: string;
  cells: ReactNode[];
}

/** The tree table of allocations (`role="treegrid"`, spec 11 6.15.8). */
function AllocationTree({
  plan,
  network,
  diagnosed,
}: {
  plan: AddressPlan;
  network: string;
  diagnosed: ReadonlySet<string>;
}) {
  const ids = useId();
  const regionKey = (r: Region) => `region:${r.provider}/${r.region}`;
  const attachmentKey = (r: Region, att: PlanAttachment) =>
    `att:${r.provider}/${r.region}/${att.deploymentId}`;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () =>
      new Set([
        ...plan.regions.map(regionKey),
        ...plan.regions.flatMap((r) =>
          r.attachments.filter((att) => diagnosed.has(att.deploymentId)).map((att) => attachmentKey(r, att)),
        ),
      ]),
  );
  const [focused, setFocused] = useState<string | null>(null);

  const rows: Row[] = [];
  for (const region of plan.regions) {
    const rk = regionKey(region);
    rows.push({
      key: rk,
      level: 1,
      parent: null,
      expandable: true,
      name: `${region.provider} · ${region.region}`,
      cells: [
        <span className="font-semibold">{`${region.provider} · ${region.region}`}</span>,
        <span className="flex flex-wrap gap-1">
          {region.blocks.map((b) => (
            <Cidr key={b} cidr={b} />
          ))}
        </span>,
        <span className="font-semibold">{a.regionBlock}</span>,
        null,
        <span className="flex items-center gap-2">
          <meter
            className="net-meter"
            min={0}
            max={100}
            value={region.allocatedPercent}
            aria-label={a.allocatedPercent(percentText(region.allocatedPercent))}
          />
          <span className="font-semibold">{a.allocatedPercent(percentText(region.allocatedPercent))}</span>
        </span>,
        null,
      ],
    });
    if (!expanded.has(rk)) continue;
    if (region.hub) {
      const hk = `hub:${region.provider}/${region.region}`;
      const inUse = region.hub.subnets.filter((x) => x.inUse).map((x) => x.role);
      const reserved = region.hub.subnets.filter((x) => !x.inUse).map((x) => x.role);
      rows.push({
        key: hk,
        level: 2,
        parent: rk,
        expandable: region.hub.subnets.length > 0,
        name: a.hub,
        cells: [a.hub, <Cidr cidr={region.hub.cidr} />, a.hubKind, null, a.hubSubnets(inUse, reserved), null],
      });
      if (expanded.has(hk))
        for (const sub of region.hub.subnets)
          rows.push({
            key: `${hk}/${sub.role}`,
            level: 3,
            parent: hk,
            expandable: false,
            name: sub.role,
            cells: [
              <span className="font-mono">{sub.role}</span>,
              <Cidr cidr={sub.cidr} />,
              null,
              null,
              sub.inUse ? a.inUse : a.reservedUse,
              null,
            ],
          });
    }
    for (const att of region.attachments) {
      const ak = attachmentKey(region, att);
      const small = tooSmall(att);
      const used = att.subnets.filter((x) => x.inUse).length;
      rows.push({
        key: ak,
        level: 2,
        parent: rk,
        expandable: att.subnets.length > 0,
        name: att.deploymentName,
        cells: [
          <Link className="link font-mono font-semibold" to={`/deployments/${att.deploymentId}`}>
            {att.deploymentName}
          </Link>,
          <Cidr cidr={att.cidr} />,
          s.sizes[att.size].toLowerCase(),
          att.attachmentState ? (
            <StatusBadge presentation={attachmentPresentation(att.attachmentState)} />
          ) : (
            <span className="badge tone-neutral">{a.retained}</span>
          ),
          a.subnets(att.subnets.length, used),
          small.length ? (
            <span className="net-need-over">
              <TriangleAlert aria-hidden className="mr-1 inline size-3.5" />
              {a.tooSmall(small.length)}
            </span>
          ) : null,
        ],
      });
      if (expanded.has(ak))
        for (const sub of att.subnets)
          rows.push({
            key: `${ak}/${sub.role}`,
            level: 3,
            parent: ak,
            expandable: false,
            name: sub.role,
            cells: [
              <span className="font-mono">{sub.role}</span>,
              <Cidr cidr={sub.cidr} />,
              null,
              null,
              sub.inUse ? a.inUse : a.reservedUse,
              <Need attachment={att} subnet={sub} />,
            ],
          });
    }
    if (region.free.length)
      rows.push({
        key: `free:${region.provider}/${region.region}`,
        level: 2,
        parent: rk,
        expandable: false,
        name: a.free,
        cells: [
          <span className="text-fg-muted">{a.free}</span>,
          <span className="font-mono text-fg-muted">{region.free.join(' · ')}</span>,
          null,
          null,
          null,
          null,
        ],
      });
  }

  const stop = rows.some((r) => r.key === focused) ? focused : (rows[0]?.key ?? null);
  const rowId = (key: string) => `${ids}-${key.replace(/[^a-zA-Z0-9-]/g, '_')}`;
  const toggle = (key: string, open?: boolean) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (open ?? !next.has(key)) next.add(key);
      else next.delete(key);
      return next;
    });
  const focus = (key: string) => {
    setFocused(key);
    requestAnimationFrame(() => document.getElementById(rowId(key))?.focus());
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, index: number) => {
    const row = rows[index];
    const isOpen = expanded.has(row.key);
    switch (event.key) {
      case 'ArrowDown':
        if (index + 1 < rows.length) focus(rows[index + 1].key);
        break;
      case 'ArrowUp':
        if (index > 0) focus(rows[index - 1].key);
        break;
      case 'Home':
        focus(rows[0].key);
        break;
      case 'End':
        focus(rows[rows.length - 1].key);
        break;
      case 'ArrowRight':
        if (row.expandable && !isOpen) toggle(row.key, true);
        else if (row.expandable && rows[index + 1]?.parent === row.key) focus(rows[index + 1].key);
        break;
      case 'ArrowLeft':
        if (row.expandable && isOpen) toggle(row.key, false);
        else if (row.parent) focus(row.parent);
        break;
      case 'Enter':
        if (row.expandable) toggle(row.key);
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  const columns = a.columns;
  return (
    <div className="table-scroll rounded-lg border border-line bg-surface">
      <table role="treegrid" aria-label={a.table(network)} className="table net-tree">
        <thead>
          <tr>
            <th scope="col">{columns.allocation}</th>
            <th scope="col">{columns.cidr}</th>
            <th scope="col">{columns.kind}</th>
            <th scope="col">{columns.state}</th>
            <th scope="col">{columns.use}</th>
            <th scope="col">{columns.need}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.key}
              id={rowId(row.key)}
              aria-level={row.level}
              aria-expanded={row.expandable ? expanded.has(row.key) : undefined}
              tabIndex={row.key === stop ? 0 : -1}
              className={`net-tree-row level-${row.level}`}
              onFocus={() => setFocused(row.key)}
              onKeyDown={(event) => onKeyDown(event, index)}
            >
              {row.cells.map((cell, i) => (
                <td key={i} role="gridcell">
                  {i === 0 ? (
                    <span className="net-tree-first" style={{ paddingLeft: `${(row.level - 1) * 18}px` }}>
                      {row.expandable ? (
                        <button
                          type="button"
                          tabIndex={-1}
                          className="net-tree-toggle"
                          aria-label={expanded.has(row.key) ? a.collapse(row.name) : a.expand(row.name)}
                          onClick={() => toggle(row.key)}
                        >
                          {expanded.has(row.key) ? (
                            <ChevronDown aria-hidden className="size-4" />
                          ) : (
                            <ChevronRight aria-hidden className="size-4" />
                          )}
                        </button>
                      ) : (
                        <span aria-hidden className="net-tree-spacer" />
                      )}
                      {cell}
                    </span>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Phones: the regions as card lists, each attachment's subnets under it (spec 11 6.15.19). */
function PhonePlan({ plan }: { plan: AddressPlan }) {
  return (
    <div className="space-y-3">
      {plan.regions.map((region) => (
        <section
          key={`${region.provider}/${region.region}`}
          aria-label={`${region.provider} · ${region.region}`}
        >
          <h3 className="font-semibold">{`${region.provider} · ${region.region}`}</h3>
          <p className="text-sm text-fg-muted">
            <span className="font-mono">{region.blocks.join(', ')}</span> ·{' '}
            {a.allocatedPercent(percentText(region.allocatedPercent))}
          </p>
          <ul className="mt-2 space-y-2">
            {region.attachments.map((att) => (
              <li key={att.deploymentId} className="net-card">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold">{att.deploymentName}</span>
                  {att.attachmentState && (
                    <StatusBadge presentation={attachmentPresentation(att.attachmentState)} />
                  )}
                </div>
                <p className="text-sm">
                  <span className="font-mono">{att.cidr}</span> · {s.sizes[att.size].toLowerCase()}
                </p>
                <ul className="mt-1 text-xs">
                  {att.subnets.map((sub) => (
                    <li key={sub.role} className="flex flex-wrap gap-x-2">
                      <span className="font-mono">{sub.role}</span>
                      <span className="font-mono">{sub.cidr}</span>
                      <span>{sub.inUse ? a.inUse : a.reservedUse}</span>
                      <Need attachment={att} subnet={sub} />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {region.free.length > 0 && (
            <p className="mt-1 text-xs text-fg-muted">
              {a.free}: <span className="font-mono">{region.free.join(' · ')}</span>
            </p>
          )}
        </section>
      ))}
    </div>
  );
}

/** The Address plan tab (spec 11 6.15.8, mock n03): supernet bars, diagnostics, the tree table, Edit. */
export function AddressPlanTab({ network }: NetworkTabProps) {
  const n = network.data;
  const plan = useAddressPlan(n.id);
  const manage = useManageNetworks();
  const phone = usePhone();
  const [history, setHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const switchId = useId();
  const settings = n.settings.addressPlan;
  const lengths: Lengths = {
    region: settings.regionPrefixLength,
    hub: settings.hubPrefixLength,
    size: s.sizes[settings.defaultAttachmentSize].toLowerCase(),
    prefix: SIZE_PREFIX[settings.defaultAttachmentSize],
  };
  const diagnosed = useMemo(
    () =>
      new Set(
        (plan.data?.diagnostics ?? [])
          .map((d) => (plan.data ? attachmentOf(plan.data, d)?.deploymentId : undefined))
          .filter((x): x is string => !!x),
      ),
    [plan.data],
  );

  if (plan.error) return <ErrorMessage error={plan.error} onRetry={() => void plan.refetch()} />;
  if (!plan.data) return <Loading />;
  const data = plan.data;
  const released = data.history.filter((h) => h.state === 'released').length;
  const edit = (
    <Button icon={<Pencil aria-hidden className="size-4" />} onClick={() => setEditing(true)}>
      {a.edit}
    </Button>
  );

  return (
    <div className="space-y-3" data-testid="address-plan">
      <div className="net-plan-top">
        <div className="card min-w-0 flex-1 space-y-3 p-3">
          {data.supernets.map((supernet) => (
            <SupernetBar key={supernet.cidr} plan={data} supernet={supernet} lengths={lengths} />
          ))}
        </div>
        <aside aria-labelledby={`${switchId}-title`} className="card net-plan-side p-3">
          <div className="flex items-center justify-between gap-2">
            <h2 id={`${switchId}-title`} className="font-semibold">
              {a.title}
            </h2>
            {manage && edit}
          </div>
          <p className="mt-1 text-sm text-fg-muted">{a.sideText}</p>
          <label className="mt-2 flex items-center gap-2 text-sm">
            <button
              type="button"
              role="switch"
              aria-checked={history}
              aria-labelledby={`${switchId}-label`}
              className="net-switch"
              onClick={() => setHistory((h) => !h)}
            >
              <span aria-hidden className="net-switch-knob" />
            </button>
            <span id={`${switchId}-label`}>{released ? a.showHistoryCount(released) : a.showHistory}</span>
          </label>
        </aside>
      </div>

      {data.diagnostics.map((d, i) => {
        const att = attachmentOf(data, d);
        const tone =
          d.severity === 'error'
            ? 'callout-danger'
            : d.severity === 'warning'
              ? 'callout-warning'
              : 'callout-info';
        return (
          <div key={`${d.code}-${i}`} className={`callout ${tone}`} data-testid="plan-diagnostic">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <p className="min-w-0 flex-1">
              {d.message} <code className="font-mono text-xs">{d.code}</code>
            </p>
            {att ? (
              <Link
                className="btn btn-sm shrink-0"
                to={`/deployments/${att.deploymentId}?tab=settings&section=network`}
              >
                {a.openSettings(att.deploymentName)}
              </Link>
            ) : d.code === 'IPAM_UTILIZATION_HIGH' && manage ? (
              <div className="shrink-0">{edit}</div>
            ) : null}
          </div>
        );
      })}

      {data.regions.length === 0 ? (
        <p className="help">{a.noRegions}</p>
      ) : phone ? (
        <PhonePlan plan={data} />
      ) : (
        <AllocationTree plan={data} network={n.name} diagnosed={diagnosed} />
      )}

      {history && (
        <section aria-labelledby={`${switchId}-history`}>
          <h2 id={`${switchId}-history`} className="mb-2 font-semibold">
            {a.history}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table className="table table-cards">
              <thead>
                <tr>
                  <th scope="col">{a.historyColumns.cidr}</th>
                  <th scope="col">{a.historyColumns.state}</th>
                  <th scope="col">{a.historyColumns.deployment}</th>
                  <th scope="col">{a.historyColumns.reusable}</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <tr key={`${h.cidr}-${h.releasedAt}`}>
                    <td>
                      <Cidr cidr={h.cidr} />
                    </td>
                    <td>{h.state === 'released' ? a.released : a.retained}</td>
                    <td>{h.deploymentName ?? strings.common.dash}</td>
                    <td>
                      {h.reusableAfter ? a.reusableAfter(formatDay(h.reusableAfter)) : strings.common.dash}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {editing && <EditAddressPlanDialog network={network} plan={data} onClose={() => setEditing(false)} />}
    </div>
  );
}
